import React from 'react';
import { first, get, isObject, isEqual, size, uniqBy, last, set, snakeCase } from '../lib/lodash';
import axios from 'axios';
import { UserPermission } from '../lib/filter';
import { buildRealmTree, getRealmIcon } from '../components/droprealms';
import { Loading } from '../styles';
import moment from 'moment';
import { Row } from '../components/row';
import { Icon } from '@mui/material';
import copy from 'copy-to-clipboard';
import normalize from 'json-api-normalizer';
import build from 'redux-object';

export const uppercaseFirst = (str = '') => {
  if (typeof str !== 'string') {
    return str;
  }
  return `${(str || '').charAt(0).toUpperCase()}${str.substring(1, str.length)}`;
};

export const uppercaseFirstAll = (str = '') => {
  if (typeof str !== 'string') {
    return (str || '').toString();
  }
  if (str.indexOf('-') > -1) {
    return str
      .split('-')
      .map((s) => uppercaseFirst(s))
      .join('-');
  } else if (str.indexOf('_') > -1) {
    return str
      .split('_')
      .map((s) => uppercaseFirst(s))
      .join(' ');
  } else {
    return uppercaseFirst(str);
  }
};

export const lowerCaseAll = (str) => {
  const snakecase = snakeCase(str);

  return snakecase.split('_').join(' ');
};

export const sortByDate = (arr, dateField) => {
  return arr.sort((a, b) => {
    const adate = get(a, dateField);
    const bdate = get(b, dateField);
    return new Date(bdate) - new Date(adate);
  });
};

export async function getVideoFrame(videoFile) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.muted = true;
    video.src = typeof videoFile == 'string' ? videoFile : URL.createObjectURL(videoFile);
    video.crossOrigin = 'anonymous';
    video.currentTime = 1;
    let resolved = false;
    let rejected = false;

    const onResolve = (res) => {
      if (rejected) {
        return;
      }

      resolved = true;
      resolve(res);
    };

    const onFail = (e) => {
      if (resolved) {
        return;
      }

      rejected = true;
      reject(e || new Error('Failed to get video frame'));
    };

    video.onloadeddata = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
        video.pause();
        const dataURL = canvas.toDataURL('image/png');
        onResolve(dataURL);
      } catch (err) {
        onFail(err);
      }
    };

    video.onerror = (e) => onFail(e);

    setTimeout(onFail, 2000);
  });
}

export const toBase64 = (file) =>
  new Promise(async (resolve, reject) => {
    if (file.type.includes('video')) {
      try {
        resolve(await getVideoFrame(file));
      } catch (err) {
        if (err.message.includes('to get video frame')) {
          await getBase64Image('https://perform-public.s3.us-east-1.amazonaws.com/1492617371-15-youtube-video-player-play-logo-media_83436.png', { addPrefix: true }).then(resolve).catch(reject);
        } else {
          reject(err);
        }
      }
    } else {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    }
  });

export const wait = (time = 1000) => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      resolve();
    }, time);
  });
};

export const fileIsImage = (file) => {
  return file && file['type'] && file['type'].split('/')[0] === 'image';
};

export const getImageDims = (file) => {
  return new Promise((resolve, reject) => {
    try {
      var url = URL.createObjectURL(file);
      var img = new Image();

      img.onload = function () {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(img.src);
      };

      img.src = url;
    } catch (err) {
      reject(err);
    }
  });
};

export const getBase64Image = (url, opts = {}) => {
  opts = {
    addPrefix: false,
    ...opts,
  };
  return axios
    .get(url, {
      responseType: 'arraybuffer',
      withCredentials: false,
    })
    .then((response) => {
      let res = new Buffer(response.data, 'binary').toString('base64');
      if (opts.addPrefix) {
        res = `data:image/png;base64,${res}`;
      }
      return res;
    });
};

export const loadBase64Image = (url, opts = {}) => {
  opts = {
    addPrefix: true,
    ...opts,
  };
  return fetch(url, { mode: 'cors', cache: 'no-cache' })
    .then((resp) => resp.arrayBuffer())
    .then((resp) => {
      let res = Buffer.from(resp).toString('base64');
      if (opts.addPrefix) {
        res = `data:image/png;base64,${res}`;
      }
      return res;
    });
};

export const getImageBlob = (url) => {
  return fetch(url, { cache: 'no-cache' }).then((res) => res.blob());
};

export const downloadImage = async (file) => {
  try {
    const src = get(file, 'large', '');
    const blob = await getImageBlob(src);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = get(file, 'name', getImageNameFromSrc(src));
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (err) {
    console.error(err);
    alert('Failed to download image.');
  }
};

export const getImageSource = (url) => {
  return `${url}`;
};

export const getImageNameFromSrc = (src) => {
  return last(`${src}`.split('?')[0].split('/'));
};

export const getImageUrl = (img) => {
  return `https://perform-3-media-dev.s3.amazonaws.com/dev/medialibrary/${img.id}/${img.fileName}`;
};

export const taskNumToString = (num) => {
  const defs = {
    ['0']: 'open',
    ['1']: 'pending',
    ['2']: 'closed',
  };

  return get(defs, num);
};

export const taskStringToNum = (string) => {
  const defs = {
    open: 0,
    pending: 1,
    closed: 2,
  };

  return get(defs, string);
};

export const getTaskStatus = (task) => {
  const obj = {
    title: 'open',
    icon: <Icon>radio_button_unchecked</Icon>,
    status: get(task, 'status', 0),
  };
  if (typeof task === 'string') {
    task = {
      status: taskStringToNum(task) || 0,
    };
  }
  if (!task) {
    return obj;
  } else if (task.status === 1) {
    obj.title = 'pending';
    obj.icon = <Icon>check_circle_outline</Icon>;
  } else if (task.status === 2) {
    obj.title = 'closed';
    obj.icon = <Icon>check_circle</Icon>;
  }
  return { ...obj, titleCase: uppercaseFirst(obj.title) };
};

export const getTaskImage = (task = {}) => {
  const images = task.images;
  const firstImage = first(task.images);
  const src = get(firstImage, 'thumbUrl', get(firstImage, 'fullUrl', ''));
  return {
    src,
    icon: size(images) > 1 && 'photo_library',
  };
};

export const getTaskTradeTypes = (task) => {
  const taskArr = get(task, 'tradeTypes', []).reduce((agg, t) => {
    agg.push(get(t, 'name') || get(t, 'defaultName') || '');
    return agg;
  }, []);
  return taskArr;
};

export const copyToClipboardNotIos = (text) => {
  try {
    var copyText = document.createElement('textarea');
    document.body.appendChild(copyText);
    copyText.style = 'display: inline; width: 1px; position: fixed; left: -999999px; top: -999999px;';
    copyText.value = text;
    copyText.focus();
    document.execCommand('SelectAll');
    document.execCommand('Copy');
    document.body.removeChild(copyText);
    if (get(navigator, 'clipboard.writeText')) {
      navigator.clipboard.writeText(text).then(() => 0);
    }
  } catch (err) {
    console.error(`Failed to copy text: ${err.message}`);
  }
};

export const isIOS = () => {
  return (
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
};

export const isNativeAppBrowser = () => {
  return navigator.userAgent.includes('perform-native/');
};

export const copyToClipboard = async (string) => {
  try {
    copy(string);
  } catch (err) {
    alert(`Failed to copy text: ${err.message}`);
  }
};

export const addNewLinesToText = (text, replaceWith = '\n') => {
  let newText = '';
  const splits = text.split('\n');
  splits.forEach((spl) => {
    if (spl === '') {
      newText += replaceWith;
    } else {
      newText += spl;
    }
  });
  return newText;
};

export const imagesToServerConversion = (images = []) => {
  return images.map((p) => ({ name: p.path, file: p.data, caption: get(p, 'caption', '') }));
};

export const stringSearch = (strToFind, arrToSearch, checkForArr) => {
  const sstring = strToFind.toLowerCase().trim();

  return arrToSearch.filter((item) => {
    const has = checkForArr.filter((a) => {
      return get(item, a, '').toString().toLowerCase().indexOf(sstring) > -1;
    });
    return size(has) > 0;
  });
};

export const letterFromAlphabet = (num, _opts = {}) => {
  const opts = {
    toUpperCase: true,
    ..._opts,
  };
  var alphabet = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z'];
  let val = alphabet[num];
  if (opts.toUpperCase) {
    val = val.toUpperCase();
  }
  return val;
};

export const stringObj = (obj) => {
  for (let k in obj) {
    const val = obj[k];

    if (typeof val === 'number') {
      obj[k] = obj[k].toString();
    }
  }

  return obj;
};

export const cleanCopy = (obj) => {
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (err) {
    return obj;
  }
};

export const parseJson = (val, fb = {}) => {
  if (val === null || val === undefined) return fb;
  if (typeof val === 'object') return val;
  try {
    return JSON.parse(val);
  } catch (err) {
    console._error('Failed to parse');
    return fb;
  }
};

export function guid() {
  try {
    function s4() {
      return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    }

    return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4();
  } catch (err) {
    return guid();
  }
}

export const getUserAppPermissions = (perms = []) => {
  if (typeof perms === 'string') {
    perms = parseJson(perms, []);
  }
  const up = UserPermission.filter((up) => (perms || []).find((pd) => get(pd, 'name', '').indexOf(up.match) > -1));
  return up.length > 0 ? up : null;
};

export const reverseUserAppPermissions = (obj) => {
  return UserPermission.filter((up) => get(obj, up.value));
};

export const appPermissionToServer = (permarray) => {
  return permarray.map(
    (p) =>
      `${get(
        UserPermission.find((up) => up.value === p),
        'match'
      )}.*`
  );
};

export const _size = (obj) => {
  if (isObject(obj) && !Array.isArray(obj)) {
    return Object.keys(obj).length;
  } else {
    return size(obj);
  }
};

export const _first = (obj) => {
  if (isObject(obj)) {
    const k = Object.keys(obj)[0];
    return obj[k];
  } else {
    return first(obj);
  }
};

export const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const validateEmail = (email) => {
  return emailRegex.test(email);
};

export const findServerPerm = (perms, match) => {
  return perms.find((p) => p.name === match);
};

// export const downloadURI = (filePath) => {
//   const link = document.createElement('a');
//   link.href = filePath;
//   link.download = filePath.substr(filePath.lastIndexOf('/') + 1);
//   link.click();
// }

export const getFirstKey = (obj) => {
  return get(obj, Object.keys(obj)[0]);
};

export const allRealmsForUserRealm = (all, realm) => {
  const tree = buildRealmTree(all);
  const index = tree.findIndex((e) => get(e, 'id') === get(realm, 'id'));
  const topRealm = get(tree, index);
  return flattenAllRealms([topRealm], 'children');
};

export const flattenAllRealms = (all, childProp = 'realms') => {
  let realms = [];

  const addToList = (arr) => {
    arr.map((a) => {
      realms.push(a);
      const arealms = get(a, childProp);
      if (arealms && get(arealms, '0.tier') !== a.tier) {
        addToList(arealms);
      }
    });
  };

  addToList(all);

  return uniqBy(realms, 'id');
};

export const flattenAllChildren = (all, childProp = 'children') => {
  let realms = [];

  const addToList = (arr) => {
    arr.map((a) => {
      realms.push(a);
      const arealms = get(a, childProp);
      if (arealms) {
        addToList(arealms);
      }
    });
  };

  addToList(all);

  return uniqBy(realms, 'id');
};

export const flattenAllRealmsWithRestricted = (all, restricted = [], opts = {}) => {
  opts = {
    treeAll: true,
    ...opts,
  };

  const realms = [];

  const add = (r) => {
    if (restricted.indexOf(r.id) === -1 && passesTier(r)) {
      realms.push(r);
    }
  };

  const passesTier = (r) => {
    if (!opts.tier) {
      return true;
    } else {
      return r.tier === opts.tier;
    }
  };

  const addToList = (arr) => {
    arr.map((a) => {
      add(a);
      if (a.children && restricted.indexOf(a.id) === -1) {
        addToList(a.children);
      }
    });
  };

  const arr = opts.treeAll ? buildRealmTree(all) : all;
  addToList(arr);

  return uniqBy(realms, 'id');
};

export const isValidHex = (str) => {
  return /^#([0-9A-F]{3}){1,2}$/i.test(str);
};

export const getValidHex = (str, replaceWith) => {
  const valid = isValidHex(str);
  return valid ? str : replaceWith;
};

export const sortUsersAlphabetically = (users) => {
  return users.sort((a, b) => {
    const aName = get(a, 'name', '').toUpperCase();
    const bName = get(b, 'name', '').toUpperCase();
    if (aName < bName) {
      return -1;
    }
    if (aName > bName) {
      return 1;
    }
    return 0;
  });
};

export const sortItemsAlphabetically = (users, sel = 'name') => {
  return users.sort((a, b) => {
    const aName = get(a, sel, '').toUpperCase();
    const bName = get(b, sel, '').toUpperCase();
    if (aName < bName) {
      return -1;
    }
    if (aName > bName) {
      return 1;
    }
    return 0;
  });
};

export const getRealmName = (realmOrTask, ifBlank = '', forceParent = false) => {
  if (!realmOrTask) {
    return Loading;
  }

  const hasRealm = get(realmOrTask, 'realm');
  const hasParentRealm = get(hasRealm, 'parent');

  if (hasRealm && (hasRealm.tier === 'lot' || forceParent) && hasParentRealm) {
    return `${get(hasParentRealm, 'name')}: ${get(hasRealm, 'name')}`;
  } else {
    return get(hasRealm, 'name', ifBlank);
  }
};

export const removeOverflowFromBody = () => {
  const elem = document.getElementsByTagName('body')[0];
  const style = elem.style;
  style.overflow = '';
};

const reA = /[^a-zA-Z]/g;
const reN = /[^0-9]/g;
export const sortAlphaNum = (_a, _b, key = 'name') => {
  const a = get(_a, key, '');
  const b = get(_b, key, '');
  let aA = (a || '').replace(reA, '').toLowerCase();
  let bA = (b || '').replace(reA, '').toLowerCase();
  // if letters are equal
  if (aA === bA) {
    //check numbers
    let aN = parseInt(a.replace(reN, ''), 10);
    let bN = parseInt(b.replace(reN, ''), 10);
    return aN === bN ? 0 : aN > bN ? 1 : -1;
  } else {
    // else, compare letters
    return aA > bA ? 1 : -1;
  }
};

export const sortByOrderKey = (_a, _b, orderKey = 'order', key = 'name') => {
  const a = get(_a, orderKey, 0);
  const b = get(_b, orderKey, 0);
  return a - b || get(_a, key, '').localeCompare(get(_b, key, ''));
};

export const isDev = () => {
  const href = get(window, 'location.href', '');
  return href.indexOf('ibacos-perform-dev') > -1;
};

export const checkCanShare = () => {
  if (navigator.canShare) {
    return true;
  } else {
    return false;
  }
};

export const getUrlParams = (url) => {
  let match;
  const search = /([^&=]+)=?([^&]*)/g;
  const decode = function (s) {
    return decodeURIComponent(s);
  };
  const searchFor = '?';
  const index = url.indexOf(searchFor) + searchFor.length;
  const str = url.substring(index, url.length);
  const query = str;

  let urlParams = {};

  while ((match = search.exec(query))) {
    urlParams[decode(match[1])] = decode(match[2]);
  }

  return urlParams;
};

export const executePrint = async () => {
  window.print();
};

export const singularize = (str = '') => {
  if (str.endsWith('s')) {
    return str.slice(0, -1);
  } else {
    return str;
  }
};

export const pluralize = (str = '') => {
  if (str.endsWith('s')) {
    return str;
  } else {
    return `${str}s`;
  }
};

export const getAvg = (grades = []) => {
  const total = (grades || []).reduce((acc, c) => acc + c, 0);
  return size(grades) === 0 ? 'N/A' : total / grades.length;
};

export const exportToCsv = (filename, rows) => {
  var processRow = function (row) {
    var finalVal = '';
    for (var j = 0; j < row.length; j++) {
      var innerValue = row[j] === null ? '' : (row[j] || '').toString();
      if (row[j] instanceof Date) {
        innerValue = row[j].toLocaleString();
      } else if (row[j] instanceof Object) {
        innerValue = get(row[j], 'name', '');
      }
      var result = innerValue.replace(/"/g, '""');
      if (result.search(/("|,|\n)/g) >= 0) result = '"' + result + '"';
      if (j > 0) finalVal += ',';
      finalVal += result;
    }
    return finalVal + '\n';
  };

  var csvFile = '';
  for (var i = 0; i < rows.length; i++) {
    csvFile += processRow(rows[i]);
  }

  var blob = new Blob([csvFile], { type: 'text/csv;charset=utf-8;' });
  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, filename);
  } else {
    var link = document.createElement('a');
    if (link.download !== undefined) {
      // feature detection
      // Browsers that support HTML5 download attribute
      var url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};

export const saveWorksheet = (filename, ws) => {
  var blob = new Blob([ws], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8;' });
  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, filename);
  } else {
    var link = document.createElement('a');
    if (link.download !== undefined) {
      // feature detection
      // Browsers that support HTML5 download attribute
      var url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};

export const reduceKeys = (obj) => {
  return Object.keys(obj)
    .map((k) => get(obj, k))
    .filter((f) => f);
};

export const stopProp = (e) => {
  if (!e) {
    return;
  }
  e.stopPropagation && e.stopPropagation();
  e.preventDefault && e.preventDefault();
};

export const getObjectDiff = (obj1, obj2) => {
  const diff = Object.keys(obj1).reduce((result, key) => {
    if (!obj2.hasOwnProperty(key)) {
      result.push(key);
    } else if (isEqual(obj1[key], obj2[key])) {
      const resultKeyIndex = result.indexOf(key);
      result.splice(resultKeyIndex, 1);
    }
    return result;
  }, Object.keys(obj2));

  return diff;
};

export const percentify = (val, fixed = 0) => {
  return (val || 0).toFixed(fixed) + '%';
};

export const getErrorMessage = (err) => {
  if (err.response) {
    const { exception, file, line, message } = get(err, 'response.data', {});
    return `
<b>Message</b>: ${message}<br />
<b>Exception</b>: ${exception}<br />
<b>File</b>: ${file}<br />
<b>Line</b>: ${line}<br />
`;
  } else {
    return err.message;
  }
};

export const scrollTop = (id = 'html') => {
  try {
    if (!id) {
      return;
    }

    const myDiv = document.querySelectorAll(id)[0];
    myDiv.scrollTop = 0;
  } catch (err) {
    console.error(err);
  }
};

export const timeGreaterThen = (time, testInMinutes = 20) => {
  const diff = moment().diff(moment(time), 'minutes');
  return diff > testInMinutes;
};

export const waitIdle = () => {
  return new Promise((resolve, reject) => {
    try {
      if (window.requestIdleCallback) {
        requestIdleCallback(resolve);
      } else {
        setTimeout(() => resolve(), 5);
      }
    } catch (err) {
      reject(err);
    }
  });
};

export const makeArray = (amt) => {
  const arr = [];
  for (let i = 0; i < amt; i++) {
    arr.push(i);
  }
  return arr;
};

export const makeArrayFrom = (start, end, step = 1) => {
  const arr = [];
  for (let i = start; i <= end; i += (step || 1)) {
    arr.push(i);
  }
  return arr;
};

export const convertToPostStructure = (obj) => {
  const newobj = {};
  const fixCase = (s) => {
    return s
      .replace(/\.?([A-Z])/g, function (x, y) {
        return '_' + y.toLowerCase();
      })
      .replace(/^_/, '');
  };
  for (let k in obj) {
    const _k = fixCase(k);
    newobj[_k] = obj[k];
  }
  return newobj;
};

export const stringIt = (it) => {
  return `${it}`;
};

export const createAppUrl = (str) => {
  return `${get(window, 'location.origin')}/${str}`;
};

export const isNewItem = (item) => {
  return (get(item, 'id', '') || '').toString().startsWith('new_');
};

export const scoringClass = (config) => {
  const simple = ['App\\Calculators\\ScorecardSimple', 'simple'];
  const isSimple = simple.includes(get(config, 'scoringClass'));

  return {
    isSimple,
    isAggregate: !isSimple,
  };
};

export const shouldUseSubEditView = (config) => {
  if (!config) {
    return false;
  }
  const { hasSubScores } = config;
  return hasSubScores;
};

export const ensureArray = (item) => {
  if (!item) {
    return [];
  }
  return Array.isArray(item) ? item : [item];
};

export const customSearch = (opts, s, opt = {}, enableCustomOption = false) => {
  const { inputValue } = s;

  if (!inputValue) {
    return opts;
  }

  const key = get(opt, 'key', 'name');

  const val = inputValue.trim().toLowerCase().split(' ');

  var filtered = opts
    .map((o) => {
      const name = (typeof key == 'function' ? key(o) : o[key] || '').toLowerCase();

      return {
        ...o,
        matches: size(val.filter((v) => name.indexOf(v) > -1).join('')),
      };
    })
    .filter((f) => !!f.matches)
    .sort((a, b) => b.matches - a.matches);

  if (enableCustomOption) {
    filtered.push({
      name: inputValue,
      label: `Add "${inputValue}"`,
    });
  }

  return filtered;
};

export const getRealmChildTier = (pTier, plural = false) => {
  const tiers = {
    builder: { child: 'Division', plural: 'Divisions' },
    division: { child: 'Community', plural: 'Communities' },
    community: { child: 'Lot', plural: 'Lots' },
    lot: { child: 'Room', plural: 'Rooms' },
    room: { child: 'Room', plural: 'Rooms' },
  };

  return get(tiers, `${pTier}.${plural ? 'plural' : 'child'}`, 'Division');
};

export function mergeDeep(...objects) {
  const isObject = (obj) => obj && typeof obj === 'object';

  return objects.reduce((prev, obj) => {
    Object.keys(obj).forEach((key) => {
      const pVal = prev[key];
      const oVal = obj[key];

      if (Array.isArray(pVal) && Array.isArray(oVal)) {
        prev[key] = oVal;
      } else if (isObject(pVal) && isObject(oVal)) {
        prev[key] = mergeDeep(pVal, oVal);
      } else {
        prev[key] = oVal;
      }
    });

    return prev;
  }, {});
}

export function mergeDeepIgnoreNulls(...objects) {
  const isObject = (obj) => obj && typeof obj === 'object';

  return objects.reduce((prev, obj) => {
    Object.keys(obj).forEach((key) => {
      const pVal = prev[key];
      const oVal = obj[key];

      if (oVal === null) {
        return;
      }

      if (Array.isArray(pVal) && Array.isArray(oVal)) {
        prev[key] = size(oVal) ? oVal : pVal;
      } else if (isObject(pVal) && isObject(oVal)) {
        prev[key] = mergeDeep(pVal, oVal);
      } else {
        prev[key] = oVal === 0 && !!pVal ? pVal : oVal;
      }
    });

    return prev;
  }, {});
}

export function realmsToString(realms, opts = {}) {
  opts = {
    includeIcon: false,
    ...opts,
  };

  if (opts.includeIcon) {
    return (realms || []).map((r) => (
      <Row>
        <div style={{ marginRight: '8px' }}>{getRealmIcon(r)}</div>
        {r.name}
      </Row>
    ));
  }

  return (realms || []).map((r) => r.name).join(', ');
}

export const pickOne = (obj, arr, keyname) => {
  if (!keyname) {
    keyname = last(arr);
  }
  const newobj = {
    [keyname]: undefined,
  };
  arr.map((k) => {
    const v = get(obj, k);
    if (v !== undefined) {
      newobj[keyname] = v;
    }
  });
  return newobj;
};

export const pickFrom = (obj, arr) => {
  const newobj = {};
  arr.map((k) => {
    set(newobj, k, get(obj, k));
  });
  return newobj;
};

export const findOne = (obj, arr) => {
  let val;

  for (let i = 0; i < arr.length; i++) {
    const k = arr[i];
    if (val !== undefined) {
      break;
    }

    const v = get(obj, k);

    if (v !== undefined) {
      val = v;
      break;
    }
  }

  return val;
};

export const numString = (v, _opts = {}) => {
  const opts = {
    length: 3,
    toFixed: 2,
    ifNaN: '0',
    allowNaNo: true,
    ..._opts,
  };
  const str = `${v}`;
  const val = parseFloat(str);

  if (isNaN(val)) {
    if (opts.allowNaNo && ['n/a', 'n/o'].includes(`${v}`.toLowerCase())) {
      return v;
    }
    return opts.ifNaN;
  }

  return str.length > opts.length ? val.toFixed(opts.toFixed) : str;
};

export const normalizeResp = (resp, opts = {}) => {
  opts = {
    name: 'tasks',
    returnSingle: true,
    ...opts,
  };
  const tdata = normalize(resp.data);
  const built = build(tdata, opts.name);
  return opts.returnSingle ? first(built) : built;
};
